import { useAppDispatch, useAppSelector } from "@app/hooks"
import { Box } from "@mui/material"
import { useEffect } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { fetchCourse, unmounted } from "@features/courseView/courseViewSlice"
import { selectMinorChanges } from "@features/coursesMenu/coursesSelectors"
import { SnackbarDuration, showSnackbarItem } from "@features/ui/uiSlice"
import CoursePreview from "./coursePreview"
import { CourseHeader } from "./courseViewHeader"
import appTheme from "../theme/appTheme"
import { parseStringVersion } from "@utils/versionUtils"
import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import {
  QueryErrorPanel,
  QueryNotFoundPanel,
} from "@cmp/loaders/queryStatePanel"

export function CourseViewContainer() {
  const course = useAppSelector((state) => state.courseView.course)
  const loadingState = useAppSelector((state) => state.courseView.loadingState)
  const { major } = course
    ? parseStringVersion(course?.version)
    : { major: undefined }
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { courseId } = useParams()

  const search = useLocation().search
  const version = new URLSearchParams(search).get("version")
  const hasChanges = useAppSelector(selectMinorChanges(course?.id, major))

  useEffect(() => {
    if (!courseId) return
    dispatch(fetchCourse({ courseId: courseId, version: version ?? "1.1" }))
    return () => {
      dispatch(unmounted())
    }
  }, [dispatch, courseId, version])

  useEffect(() => {
    if (!course) {
      return
    }
    if (hasChanges) {
      dispatch(
        showSnackbarItem({
          message: "This course has unpublished changes",
          type: "success",
          duration: SnackbarDuration.short,
          anchorOrigin: "bottom",
        }),
      )
    }
  }, [dispatch, course, hasChanges])

  if (loadingState === "not-found") {
    return (
      <QueryNotFoundPanel
        title="We couldn't find this course."
        description="It may have been deleted, or the link may be wrong."
        backLabel="Back to courses"
        onBack={() => navigate("/courses")}
      />
    )
  }

  if (loadingState === "error") {
    return (
      <QueryErrorPanel
        message="We couldn't load this course."
        onRetry={() =>
          courseId &&
          dispatch(fetchCourse({ courseId, version: version ?? "1.1" }))
        }
      />
    )
  }

  if (!course) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingComponent loadedComponents="course" />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        bgcolor: appTheme.palette.eTypes.sand15,
      }}
    >
      <CourseHeader />
      <Box
        sx={{
          display: "flex",
          width: "40%",
          maxWidth: "600px",
          height: "100%",
          justifyContent: "center",
          overflow: "hidden",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <CoursePreview course={course} />
      </Box>
    </Box>
  )
}
